/**
 * A read that failed, said where its content would be — with a retry: the one
 * error state of every portal, so a failed read never reads as "nothing here".
 *
 *   `section` — in place of a whole section or page (an icon, a title, a line);
 *   `inline`  — in place of a part of a card (one line and the retry beside it).
 *
 * Presentation only: every string is the caller's, translated (no i18n here).
 */
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../primitives/empty';

export interface SectionErrorStateProps {
  title: string;
  description?: string;
  /** The read's `refetch` — without it there is no retry to offer. */
  onRetry?: () => void;
  retryLabel: string;
  /** While the retry is on its way. */
  retrying?: boolean;
  size?: 'section' | 'inline';
  /** A way out beside the retry (e.g. "Go back"), the caller's button. */
  secondaryAction?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function SectionErrorState({
  title,
  description,
  onRetry,
  retryLabel,
  retrying = false,
  size = 'section',
  secondaryAction,
  className,
  'data-testid': testId,
}: SectionErrorStateProps) {
  const retry =
    onRetry || secondaryAction ? (
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry} loading={retrying}>
            {retryLabel}
          </Button>
        ) : null}
        {secondaryAction}
      </div>
    ) : null;

  if (size === 'inline') {
    return (
      <div
        role="alert"
        data-testid={testId}
        className={cn(
          'flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-4',
          className
        )}
      >
        <div className="flex min-w-0 items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-tone-amber" aria-hidden="true" />
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-medium text-foreground">{title}</p>
            {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
          </div>
        </div>
        {retry}
      </div>
    );
  }

  return (
    <Empty role="alert" data-testid={testId} className={cn('border bg-muted/30', className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <AlertTriangle className="text-tone-amber" aria-hidden="true" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
      {retry ? <EmptyContent>{retry}</EmptyContent> : null}
    </Empty>
  );
}
